const DEFAULT_MIN_DISTANCE = 50;
const DEFAULT_AXIS_RATIO = 1.15;

let documentScrollLockCount = 0;
let documentScrollSnapshot = null;

const readInlineStyles = (element, properties) =>
  Object.fromEntries(properties.map((property) => [property, element.style[property]]));

const restoreInlineStyles = (element, snapshot) => {
  Object.entries(snapshot || {}).forEach(([property, value]) => {
    element.style[property] = value;
  });
};

const ROOT_LOCK_PROPERTIES = ["overflow", "overscrollBehavior"];
const BODY_LOCK_PROPERTIES = [
  "overflow",
  "overscrollBehavior",
];

export const acquireDocumentScrollLock = () => {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return () => {};
  }

  if (documentScrollLockCount === 0) {
    const root = document.documentElement;
    const body = document.body;
    const scrollingElement = document.scrollingElement || root;
    const scrollX = window.scrollX || scrollingElement.scrollLeft || 0;
    const scrollY = window.scrollY || scrollingElement.scrollTop || 0;

    documentScrollSnapshot = {
      root,
      body,
      rootStyles: readInlineStyles(root, ROOT_LOCK_PROPERTIES),
      bodyStyles: readInlineStyles(body, BODY_LOCK_PROPERTIES),
      scrollX,
      scrollY,
    };

    root.style.overflow = "hidden";
    root.style.overscrollBehavior = "none";
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
  }

  documentScrollLockCount += 1;
  let released = false;

  return () => {
    if (released || documentScrollLockCount === 0) return;
    released = true;
    documentScrollLockCount -= 1;
    if (documentScrollLockCount !== 0 || !documentScrollSnapshot) return;

    const snapshot = documentScrollSnapshot;
    documentScrollSnapshot = null;
    restoreInlineStyles(snapshot.root, snapshot.rootStyles);
    restoreInlineStyles(snapshot.body, snapshot.bodyStyles);
    if (window.scrollX !== snapshot.scrollX || window.scrollY !== snapshot.scrollY) {
      window.scrollTo(snapshot.scrollX, snapshot.scrollY);
    }
  };
};

export const classifyOverlaySwipe = ({
  startX,
  startY,
  endX,
  endY,
  minDistance = DEFAULT_MIN_DISTANCE,
  axisRatio = DEFAULT_AXIS_RATIO,
}) => {
  const deltaX = endX - startX;
  const deltaY = endY - startY;
  const absX = Math.abs(deltaX);
  const absY = Math.abs(deltaY);

  if (absX >= minDistance && absX > absY * axisRatio) {
    return deltaX < 0 ? "slide-next" : "slide-prev";
  }
  if (absY >= minDistance && absY > absX * axisRatio) {
    return deltaY < 0 ? "item-next" : "item-prev";
  }
  return null;
};

export const findScrollableAncestor = (target, boundary) => {
  let node = typeof Element !== "undefined" && target instanceof Element
    ? target
    : null;
  while (node && node !== boundary) {
    const style = window.getComputedStyle(node);
    const scrollable =
      /(auto|scroll)/.test(style.overflowY) &&
      node.scrollHeight > node.clientHeight + 2;
    if (scrollable) return node;
    node = node.parentElement;
  }
  return null;
};

export const canLeaveScrollableContent = ({ target, boundary, direction }) => {
  const scroller = findScrollableAncestor(target, boundary);
  if (!scroller) return true;
  if (direction === "next") {
    return (
      scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2
    );
  }
  return scroller.scrollTop <= 2;
};

export const findAdjacentFeedItem = (items, currentId, direction) => {
  if (!Array.isArray(items) || !currentId) return null;
  const index = items.findIndex((item) => item?.id === currentId);
  if (index < 0) return null;
  return items[index + (direction === "next" ? 1 : -1)] || null;
};

export const ITEM_OVERLAY_HISTORY_KEY = "unitItemOverlayEntry";

const itemOverlayQuery = (route, itemId, itemOrgId = null) => {
  const query = { ...route.query, item: itemId };
  if (itemOrgId) query.itemOrg = itemOrgId;
  else delete query.itemOrg;
  return query;
};

export const openItemOverlayRoute = (router, route, itemId, itemOrgId = null) => {
  if (!itemId) return Promise.resolve();
  const target = { query: itemOverlayQuery(route, itemId, itemOrgId) };

  if (route.query?.item) return router.replace(target);

  return router.push({
    ...target,
    state: { [ITEM_OVERLAY_HISTORY_KEY]: true },
  });
};

export const replaceItemOverlayRoute = (
  router,
  route,
  itemId,
  itemOrgId = null,
) => {
  if (!itemId) return Promise.resolve();
  return router.replace({ query: itemOverlayQuery(route, itemId, itemOrgId) });
};

export const closeItemOverlayRoute = (
  router,
  route,
  historyState = typeof window !== "undefined" ? window.history.state : null,
) => {
  if (historyState?.[ITEM_OVERLAY_HISTORY_KEY] === true) {
    router.back();
    return "back";
  }

  const { item: _item, itemOrg: _itemOrg, ...query } = route.query || {};
  router.replace({ query });
  return "replace";
};

export const restoreRoundRobinFeedOrder = (columns) => {
  if (!Array.isArray(columns) || columns.length === 0) return [];
  const maxColumnLength = Math.max(
    0,
    ...columns.map((column) => Array.isArray(column) ? column.length : 0),
  );
  const items = [];
  for (let rowIndex = 0; rowIndex < maxColumnLength; rowIndex += 1) {
    columns.forEach((column) => {
      const item = Array.isArray(column) ? column[rowIndex] : null;
      if (item?.id) items.push(item);
    });
  }
  return items;
};

export const mergeFeedNavigationContext = ({
  currentItems,
  visibleItems,
  currentId,
}) => {
  const existing = Array.isArray(currentItems) ? currentItems : [];
  const visible = Array.isArray(visibleItems) ? visibleItems : [];
  if (!currentId || visible.some((item) => item?.id === currentId)) {
    return [...visible];
  }

  const visibleById = new Map(
    visible.filter((item) => item?.id).map((item) => [item.id, item]),
  );
  const merged = existing.map((item) => visibleById.get(item?.id) || item);
  const knownIds = new Set(merged.map((item) => item?.id).filter(Boolean));
  visible.forEach((item) => {
    if (item?.id && !knownIds.has(item.id)) {
      merged.push(item);
      knownIds.add(item.id);
    }
  });
  return merged;
};
